import {
  IonButtons,
  IonCol,
  IonContent,
  IonFab,
  IonFabButton,
  IonHeader,
  IonIcon,
  IonMenuButton,
  IonPage,
  IonRow,
  IonTitle,
  IonToolbar,
} from '@ionic/react';
import Accordion from 'components/Accordion';
import WorkOrderCard from 'components/WorkOrderCard';
import AuthorizeRequired from 'containers/AuthorizeRequired';
import { useWorkOrders } from 'hooks/useWorkOrders';
import { useWorkspace } from 'hooks/useWorkspace';
import { AccountAction } from 'interfaces/AccountAction';
import { add } from 'ionicons/icons';
import React, { useMemo, useRef } from 'react';
import { createUseStyles } from 'react-jss';
import { useHistory } from 'react-router-dom';
import AutoSizer from 'react-virtualized-auto-sizer';
import { ListChildComponentProps, VariableSizeList as List } from 'react-window';

import {
  Group,
  Row,
  columnPadding,
  threeColumnBreakpoint,
  useVirtualizedList,
} from './useVirtualizedList';

const useStyles = createUseStyles({
  cardColumn: {
    [`@media (min-width: 1200px) and (max-width: ${threeColumnBreakpoint - 0.02}px)`]: {
      flex: '0 0 50% !important',
      width: '50% !important',
      maxWidth: '50% !important',
    },
  },
});

const ActiveList: React.FC = () => {
  const classes = useStyles();
  const pageRef = useRef<HTMLElement>(null);

  const history = useHistory();

  const { workspace } = useWorkspace();

  const { workorders } = useWorkOrders({
    organizationId: workspace.organizationId,
    active: true,
  });

  // Group work orders by mine and my team
  const groups = useMemo(() => {
    const { operatorId, teamId, teams } = workspace;

    const myTeam = teams.find((t) => t.id === teamId);

    const groupsDict = workorders.reduce<{ [label: string]: Group }>((acc, wo) => {
      let label = 'No Team';
      if (wo.assignee?.id === operatorId) {
        label = 'Mine';
      } else if (wo.team?.name) {
        label = wo.team.name;
      }

      if (!acc[label]) {
        acc[label] = { label, items: [], isDefaultExpanded: false };
      }

      acc[label].items.push(wo);

      return acc;
    }, {});

    const sorted = Object.values(groupsDict).sort((a, b) => {
      const getPriority = (label: string) => {
        switch (label) {
          case 'Mine':
            return 0;
          case myTeam?.name:
            return 1;
          case 'No Team':
            return 99;
          default:
            return 2;
        }
      };

      const priorityA = getPriority(a.label);
      const priorityB = getPriority(b.label);

      if (priorityA !== priorityB) {
        return priorityA - priorityB;
      }

      return a.label.localeCompare(b.label);
    });

    // Open the first two groups by default: Mine and My Team, or whatever's
    // next if either doesn't exist. The sort above already puts them in that
    // priority order, so the first two groups in the list are always the
    // right ones to open.
    sorted.slice(0, 2).forEach((group) => {
      group.isDefaultExpanded = true;
    });

    return sorted;
  }, [workspace, workorders]);

  const { listRef, rows, isExpanded, toggleGroup, itemSize } = useVirtualizedList(groups);

  const renderRow = ({ index, style, data }: ListChildComponentProps) => {
    const row: Row = data[index];

    if (row.type === 'spacer') {
      return <div style={style as React.CSSProperties} />;
    }

    if (row.type === 'header') {
      const { group } = row;

      return (
        <Accordion
          label={group.label}
          count={group.items.length}
          expanded={isExpanded(group)}
          onToggle={() => toggleGroup(group)}
          style={style as React.CSSProperties}
        />
      );
    }

    return (
      <div style={style as React.CSSProperties}>
        <IonRow style={{ padding: 0, margin: 0 }}>
          {row.items.map((workorder) => (
            <IonCol
              sizeXs="12"
              sizeMd="6"
              sizeXl="4"
              className={classes.cardColumn}
              key={workorder.id}
              style={{ padding: `${columnPadding}px` }}
            >
              <WorkOrderCard {...workorder} pageRef={pageRef} />
            </IonCol>
          ))}
        </IonRow>
      </div>
    );
  };

  return (
    <IonPage ref={pageRef}>
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonMenuButton />
          </IonButtons>
          <IonTitle>Work Orders</IonTitle>
        </IonToolbar>
      </IonHeader>

      <IonContent scrollY={false}>
        <AuthorizeRequired required={[AccountAction.GetWorkOrder, AccountAction.ListWorkOrders]}>
          <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
            <div style={{ flex: '1 1 0%' }}>
              <AutoSizer>
                {({ height, width }) => (
                  <List
                    ref={listRef}
                    height={height}
                    width={width}
                    itemData={rows}
                    itemCount={rows.length}
                    itemSize={itemSize}
                  >
                    {renderRow}
                  </List>
                )}
              </AutoSizer>
            </div>
          </div>
        </AuthorizeRequired>

        <AuthorizeRequired required={[AccountAction.CreateWorkOrder]}>
          <IonFab vertical="bottom" horizontal="end" slot="fixed">
            <IonFabButton onClick={() => history.push(`/workorders/create`)}>
              <IonIcon icon={add} />
            </IonFabButton>
          </IonFab>
        </AuthorizeRequired>
      </IonContent>
    </IonPage>
  );
};

export default ActiveList;
